/**
 * Normalize backend errors (FastAPI + Pydantic) into a readable string.
 *
 * FastAPI returns `detail` as either:
 *   • a plain string (HTTPException raises) — e.g. "Email already registered"
 *   • an array of validation error objects (Pydantic 422) — e.g.
 *     [{ type, loc: ["body","password"], msg, input, ctx }, ...]
 *
 * Without normalization, the array gets coerced to "[object Object]" or
 * disappears entirely on toast.error(), making real validation failures look
 * like generic crashes.
 */

const FIELD_LABELS_TR = {
  email: 'E-posta',
  password: 'Şifre',
  first_name: 'Ad',
  last_name: 'Soyad',
  organization_name: 'Şirket adı',
};

const FIELD_LABELS_EN = {
  email: 'Email',
  password: 'Password',
  first_name: 'First name',
  last_name: 'Last name',
  organization_name: 'Organization name',
};

const cleanMsg = (msg, lang) => {
  if (!msg) return '';
  // Pydantic v2 prefixes with "Value error, ..." — strip it.
  let m = String(msg).replace(/^Value error,\s*/i, '');
  // Localize a few common Pydantic messages to TR
  if (lang === 'tr') {
    m = m
      .replace(/^String should have at least (\d+) characters?$/i, 'En az $1 karakter olmalı')
      .replace(/^String should have at most (\d+) characters?$/i, 'En fazla $1 karakter olmalı')
      .replace(/^Field required$/i, 'Bu alan zorunlu')
      .replace(/^value is not a valid email address.*$/i, 'Geçerli bir e-posta adresi girin')
      .replace(/^Password must be at least 6 characters$/i, 'Şifre en az 6 karakter olmalı')
      .replace(/^Password is too common.*$/i, 'Şifreniz çok yaygın — daha güçlü bir şifre seçin');
  }
  return m;
};

/**
 * Extract a single user-facing error message from an axios error.
 *
 * @param {unknown} error - Caught axios error (or any thrown value).
 * @param {string} fallback - Friendly default if nothing useful is found.
 * @param {string} lang - 'tr' or 'en' — controls field labels and localized phrasing.
 * @returns {string}
 */
export const extractApiError = (error, fallback = 'Bir hata oluştu', lang = 'tr') => {
  const detail = error?.response?.data?.detail;
  const labels = lang === 'en' ? FIELD_LABELS_EN : FIELD_LABELS_TR;

  if (!detail) {
    // Network failure or non-JSON response
    if (error?.message === 'Network Error') {
      return lang === 'tr' ? 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.' : 'Cannot reach the server. Please check your connection.';
    }
    return fallback;
  }

  // Plain string detail (HTTPException) — use as-is, but localize a few well-known phrases
  if (typeof detail === 'string') {
    if (lang === 'tr') {
      if (detail === 'Email already registered') return 'Bu e-posta zaten kayıtlı. Giriş yapmayı deneyin.';
      if (detail === 'Invalid email or password') return 'E-posta veya şifre hatalı.';
      if (detail === 'Email not found') return 'Bu e-posta sistemde bulunamadı.';
      if (detail.startsWith('Too many login attempts')) return 'Çok fazla başarısız giriş denemesi. Lütfen bir süre sonra tekrar deneyin.';
    }
    return detail;
  }

  // Pydantic validation error array
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => {
        const field = Array.isArray(d?.loc)
          ? d.loc.filter((p) => p !== 'body').pop()
          : null;
        const label = field ? labels[field] || field : null;
        const msg = cleanMsg(d?.msg, lang);
        return label ? `${label}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (messages.length) return messages.join(' · ');
  }

  return fallback;
};
